import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { isMobileOnly } from 'react-device-detect';
import styled, { css } from 'styled-components';
import { CenterBox, FlexBox, Icons, Heading4 } from '@docon/docon-ui-library';
import {
  cleverTapAnl,
} from 'utilities';
import { bgColor, colors } from 'utilities/colorConfig';
import { EventAppSubModuleSelect } from 'utilities/clevertapEvent';

const tabs = [
  { label: 'Home', value: 'dashboard', icon: Icons.Home2Icon },
  { label: 'My Doctors', value: 'doctors', icon: Icons.DoctorPlusIcon },
  { label: 'Chat', value: 'chat', icon: Icons.ChatCircularIcon },
  { label: 'Appointment', value: 'appointment', icon: Icons.CalenderIcon },
  { label: 'Records', value: 'records', icon: Icons.RecordIcon },
  { label: 'Support', value: 'support', icon: Icons.InfoOuterIcon },
];

const pageMap = {
  '/dashboard': 'home',
  '/doctors': 'my_doctors',
  '/doctors/search': 'doctors_search',
  '/chat': 'chat',
  '/appointment': 'appointments',
  '/records': 'medical_records',
  '/support': 'help',
  '/support/appointment': 'support_appointments',
};

const Root = styled(FlexBox)`
  gap: 5px;
  padding: 10px 20px 20px 0;
  min-width: 300px;
  flex-direction: column;
  flex: 0 0 auto;
  background-color: ${colors.white};
  box-shadow: 0 -3px 4px 0 rgb(23 23 77 / 10%);
  --activeNavBg: ${bgColor.primaryLight};

  .navLink {
    display: flex;
    gap: 10px;
    padding: 5px 1.5rem;
    border-radius: 0 8px 8px 0;
    align-items: center;
    font-weight: 700;
    color: ${colors.subHeading};
  }

  .isActive {
    color: ${colors.primaryBlue};
    background-color: var(--activeNavBg);
  }

  ${() => isMobileOnly && css`
    padding: 5px;
    flex-direction: row;
    justify-content: space-around;
    order: 1;
    z-index: 1;
    --activeNavBg: transparent;

    .navLink {
      gap: 0;
      padding: 0;
      font-weight: 500;
      flex-direction: column;
    }

    ${Heading4} {
      font-size: 12px;
      line-height: 16px;
    }
  `}

  @media screen and (max-width: 800px) and (min-width: 500px) {
    min-width: 220px;
  }
`;

function Item({
  item, onClick, ...props
}: any) {
  const { chatData } = useSelector(state => state.auth);
  const isChatActive = !!chatData?.followUpChatListPaginated?.data?.length;

  function handleClick() {
    onClick(item);
  }

  if (!isChatActive && item.value === 'chat') return null;

  const IconNode = item.icon;
  return (
    <NavLink
      to={`/${item.value}`}
      onClick={handleClick}
      {...props}
    >
      <CenterBox size={32}>
        <IconNode width={20} />
      </CenterBox>
      <Heading4 title={item.label} className='ellipsis' color='currentColor' />
    </NavLink>
  );
}

const docProfileRegex = /.*?doctors\/(.w?)/g;
const supportAppointment = /.*?support\/appointment\/(.d?)/g;

function getPageName(pathname: string) {
  const pageName = pageMap[pathname];
  if (pageName) return pageName;

  if (docProfileRegex.exec(pathname))
    return 'doctor_profile';
  if (supportAppointment.exec(pathname))
    return 'support_appointment';
}

function BottomNavBar({ history, location }: RouteComponentProps) {
  function handleClick({ value }: any) {
    if (value) {
      const pageName = getPageName(location.pathname);
      cleverTapAnl.event({
        event_name: 'app_sub_module_select',
        event: {
          page_name: pageName,
          module_switched_from: pageName,
          module_switched_to: pageMap[`/${value}`],
        },
      } as EventAppSubModuleSelect);
      history.replace(`/${value}`);
    }
  }

  return (
    <Root>
      {
        tabs.map((tab) => (
          <Item
            key={tab.value}
            item={tab}
            className='navLink'
            activeClassName='isActive'
            onClick={handleClick}
          />
        ))
      }
    </Root>
  );
}

export default withRouter(BottomNavBar);